import { FC } from 'react';
import { Box, BoxProps, Flex, Text, Skeleton } from '@chakra-ui/react';
import { ICON_BY_NAME } from '../Icons/Icon';
import { useGameInfo } from 'hooks/useGameInfo';
interface RuleSectionProps {
  icon: React.ReactNode;
  title: string;
  content: string | React.ReactNode;
  styles: {
    textColor: string;
    secondaryTextColor: string;
  };
}
const Rules = [{
  name: 'Rules',
  icon: ICON_BY_NAME.checkall,
  key: 'rules',
  getContent: data => data['rules']
}, {
  name: 'Game Duration',
  icon: ICON_BY_NAME.timer,
  key: 'duration',
  getContent: data => `${data['gameStart']} - ${data['gameEnd']}`
}, {
  name: 'Guide Completion',
  icon: ICON_BY_NAME.gamecards,
  key: 'guideRules',
  getContent: data => data['guideRules']
}, {
  name: 'Threshold',
  icon: ICON_BY_NAME.bolt,
  key: 'thresholdRules',
  getContent: data => data['thresholdRules']
}, {
  name: 'Objective Requirements',
  icon: ICON_BY_NAME.target,
  key: 'objectiveRequirements',
  getContent: data => data['objectiveRequirements']
}, {
  name: 'Funding Source',
  icon: ICON_BY_NAME.moneybagdiamond,
  key: 'fundingSource',
  getContent: data => data['fundingSource'],
  managerOnly: true
}];
const RuleSection: FC<RuleSectionProps> = ({
  icon,
  title,
  content,
  styles
}) => <Flex data-sentry-element="Flex" data-sentry-component="RuleSection" data-sentry-source-file="CbGameRules.tsx">
    <Box mr={4} data-sentry-element="Box" data-sentry-source-file="CbGameRules.tsx">{icon}</Box>
    <Box data-sentry-element="Box" data-sentry-source-file="CbGameRules.tsx">
      <Text color={styles.textColor} fontWeight="bold" fontSize={{
      md: 'lg'
    }} data-sentry-element="Text" data-sentry-source-file="CbGameRules.tsx">
        {title}
      </Text>
      <Box color={styles.secondaryTextColor} fontSize="lg" lineHeight="shorter" mt={1} data-sentry-element="Box" data-sentry-source-file="CbGameRules.tsx">
        {content}
      </Box>
    </Box>
  </Flex>;
export interface CbGameRulesProps extends BoxProps {
  campaignId: number;
  userId?: number;
  isManage?: boolean;
  isPending?: boolean;
  isDarkMode?: boolean;
}
export const CbGameRules: FC<CbGameRulesProps> = ({
  campaignId,
  userId,
  isManage,
  isPending,
  isDarkMode,
  ...props
}) => {
  const {
    data,
    isLoading,
    error
  } = useGameInfo(campaignId, userId);
  const rewardsSection = data?.rewards?.length ? {
    name: 'Rewards',
    icon: ICON_BY_NAME.award,
    key: 'rewards',
    getContent: () => <Box as="ul" pl={5} style={{
      listStyleType: 'disc'
    }}>
            {data.rewards.map((reward, index) => <li key={index} style={{
        marginBottom: '0.5rem'
      }}>
                {reward}
              </li>)}
          </Box>,
    managerOnly: false
  } : null;
  const rules = Rules.map(rule => rule.key === 'guideRules' ? {
    ...rule,
    name: 'Prerequisites'
  } : rule);
  const combinedRules = [...rules, ...(rewardsSection ? [rewardsSection] : [])];
  if (isPending || isLoading) {
    return <Skeleton startColor="darkMode.400" endColor="darkMode.700" borderRadius="3xl" width="100%" minHeight="280px" {...props} />;
  }
  if (error || !data) {
    return <Box {...props}>
        <Text color="red.500">Failed to load game rules.</Text>
      </Box>;
  }
  const {
    name
  } = data;
  const styles = {
    headerBackgroundColor: isDarkMode ? 'darkMode.500' : 'white',
    headerTextColor: isDarkMode ? 'white' : 'cloud.700',
    backgroundColor: isDarkMode ? 'darkMode.500' : 'white',
    borderColor: isDarkMode ? 'transparent' : 'cloud.400',
    textColor: isDarkMode ? 'text.primaryWhite' : 'text.primary',
    secondaryTextColor: isDarkMode ? 'cloud.400' : 'cloud.700'
  };
  return <Box backgroundColor={styles.backgroundColor} borderRadius="3xl" boxShadow="standardCard" color={styles.textColor} overflow="auto" border="1px solid" borderColor={styles.borderColor} {...props} data-sentry-element="Box" data-sentry-component="CbGameRules" data-sentry-source-file="CbGameRules.tsx">
      <Flex alignItems="center" backgroundColor={styles.headerBackgroundColor} borderTopRadius="3xl" borderBottom="1px solid" borderColor={styles.borderColor} px={{
      base: 4,
      lg: 6
    }} padding={'var(--chakra-space-2)'} justifyContent="center" position="sticky" top="0px" data-sentry-element="Flex" data-sentry-source-file="CbGameRules.tsx">
        <Text fontWeight="bold" color={styles.headerTextColor} paddingTop="var(--chakra-space-2)" fontSize="lg" data-sentry-element="Text" data-sentry-source-file="CbGameRules.tsx">
          {name.toUpperCase()}
        </Text>
      </Flex>
      <Box py={4} px={6} gap={6} display="flex" flexDirection={'column'} data-sentry-element="Box" data-sentry-source-file="CbGameRules.tsx">
        {combinedRules.map(({
        name,
        icon,
        key,
        getContent,
        managerOnly = false
      }) => {
        if (managerOnly && !isManage) return null;
        const content = getContent(data);
        if (!content) return null;
        return <RuleSection key={key} icon={icon({
          'aria-label': `${name} Icon`,
          height: '24px',
          width: '24px'
        })} title={name} content={content} styles={styles} />;
      })}
      </Box>
    </Box>;
};